import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { MessageHelper } from 'src/app/helpers/message-helper';
import { TranslatorService } from 'src/core/translator/domain/translator-service';
import { Nullable } from 'src/core/common/domain/types/types';
import { GetTask } from 'src/core/tasks/application/get/get-task';
import { TaskService } from 'src/core/tasks/domain/task.service';
import { Task } from 'src/core/master-data/domain/task';
import { MasterDataService } from 'src/core/master-data/domain/master-data-service';
import { canTaskBeOpenedFromNotification } from 'src/core/master-data/domain/task-available-actions';
import { GetUnreadNotificationsCount } from 'src/core/notifications/application/unread-count/get-unread-notifications-count';
import { MarkNotificationRead } from 'src/core/notifications/application/read/mark-notification-read';
import { EventsService } from 'src/core/events/events.service';
import { TemporaryTaskChangedEvent } from 'src/core/events/temporary-task-changed.event';
import { TaskNotFoundException } from 'src/core/common/domain/exceptions/task-not-found-exception';
import {
  PushNotification,
  PushNotificationActions,
  PushNotificationSource,
  PushNotificationSources,
  PushNotificationValues,
} from './push-notification';

@Injectable({
  providedIn: 'root',
})
export class PushNotificationHandlerService {
  constructor(
    private readonly messageHelper: MessageHelper,
    private readonly translation: TranslatorService,
    private readonly getTask: GetTask,
    private readonly taskService: TaskService,
    private readonly masterDataService: MasterDataService,
    private readonly router: Router,
    private readonly events: EventsService,
    private readonly ngZone: NgZone,
    private readonly getUnreadNotificationsCount: GetUnreadNotificationsCount,
    private readonly markNotificationRead: MarkNotificationRead
  ) {}

  async handleNotification(notification: PushNotification, source: PushNotificationSource): Promise<void> {
    if (source === PushNotificationSources.FOREGROUND) {
      await this.handleForegroundNotification(notification);
      return;
    }

    await this.handleBackgroundNotification(notification);
  }

  /**
   * App in foreground: we do NOT navigate automatically. We show a toast with a "View"
   * button and only navigate when the user taps it (same UX as gaudi).
   */
  private async handleForegroundNotification(notification: PushNotification): Promise<void> {
    // A new incoming push likely adds an in-app notification: refresh the header badge.
    try {
      await this.getUnreadNotificationsCount.execute();
    } catch (error) {
      console.warn('[PushNotificationHandlerService] Could not refresh unread count', error);
    }

    const actionText = this.translation.translate('NOTIFICATIONS.VIEW');
    const message = notification.body || notification.title;
    await this.messageHelper.showInfoWithAction(message, actionText, async () => {
      await this.process(notification);
    });
  }

  /**
   * User tapped the notification (app in background): navigate directly.
   */
  private async handleBackgroundNotification(notification: PushNotification): Promise<void> {
    await this.process(notification);
  }

  /**
   * Public seam to process an in-app notification tap the same way as a push notification,
   * dispatching on its custom action/value/id. Returns whether it took the user somewhere, so the
   * notifications modal can stay open when the notification led nowhere.
   */
  async processInAppNotification(action: string, value: string, id: Nullable<string>): Promise<boolean> {
    // The in-app modal marks the notification read on its own before calling this, so we pass
    // notificationId: null to avoid a redundant markRead in process().
    return await this.process({ title: '', body: '', action, value, id, notificationId: null });
  }

  private async process(notification: PushNotification): Promise<boolean> {
    await this.markAsReadIfNeeded(notification.notificationId);

    switch (notification.action) {
      case PushNotificationActions.NAVIGATE:
        return await this.processNavigation(notification);
      default:
        console.warn('[PushNotificationHandlerService] Notification action not implemented:', notification.action);
        return false;
    }
  }

  /**
   * Mark the in-app notification as read when the push carries its id (custom_notification_id).
   * A read failure must not block navigating to the task, so it is swallowed here (same criterion
   * as the notifications modal).
   */
  private async markAsReadIfNeeded(notificationId: Nullable<string>): Promise<void> {
    if (notificationId === null) {
      return;
    }

    try {
      await this.markNotificationRead.execute(notificationId);
    } catch (exception) {
      console.warn('[PushNotificationHandlerService] markRead failed:', exception);
    }
  }

  private async processNavigation(notification: PushNotification): Promise<boolean> {
    switch (notification.value) {
      case PushNotificationValues.TASKS:
        return await this.openTask(notification.id);
      default:
        console.warn('[PushNotificationHandlerService] Navigation value not implemented:', notification.value);
        return false;
    }
  }

  /**
   * Find the task pointed to by the notification, store it as the temporary task and navigate to
   * the tasks list, which renders it as a single card. A missing task shows a specific error;
   * a task that is already done and offers nothing left to do is not opened at all; any other error
   * propagates to the global CustomErrorHandler.
   *
   * Returns whether the task was actually opened.
   */
  async openTask(taskId: Nullable<string>): Promise<boolean> {
    if (taskId === null) {
      console.warn('[PushNotificationHandlerService] openTask called without a task id');
      return false;
    }

    let task: Task;
    try {
      task = await this.findTaskToOpen(taskId);
    } catch (exception) {
      if (exception instanceof TaskNotFoundException) {
        await this.messageHelper.showError(this.translation.translate('MESSAGES.TASK_NOT_FOUND'));
        return false;
      }
      throw exception; // otherwise, propagate to the global error handler
    }

    if (!canTaskBeOpenedFromNotification(task, this.masterDataService.getAvailableActions())) {
      // Tell the user instead of opening a card they can do nothing with. They stay where they are.
      await this.messageHelper.showInfo(this.translation.translate('MESSAGES.TASK_IS_ALREADY_CLOSED'));
      return false;
    }

    // Persist it as the temporary task (memory + storage) so the tasks list can render it.
    await this.taskService.saveTemporaryTask(task);

    // FCM callbacks run outside the Angular zone, so navigation and event emission must run inside it.
    await this.ngZone.run(async () => {
      await this.router.navigateByUrl('/tabs/tasks');
      this.events.publishTemporaryTaskChanged(new TemporaryTaskChangedEvent());
    });

    return true;
  }

  // The stored copy may be out of date. That is accepted so a notification can be opened offline; the API
  // is only asked for tasks the device does not have.
  private async findTaskToOpen(taskId: string): Promise<Task> {
    const storedTask = this.taskService.findById(taskId);
    if (storedTask !== null) {
      return storedTask;
    }

    const task = await this.getTask.execute(taskId);
    return task;
  }
}
